import { CommonModule, NgClass } from '@angular/common';
import { Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { SpaceLocationComponent } from '@locations';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { BuildingPlanComponent, ConnectedDevicesComponent, DataPointComponent, DeviceChartComponent, DevicesComponent, DeviceStatsComponent, GaugeComponent, MapComponent, RoomStateComponent, SpaceDetailsComponent } from '../../components';
import { type LayoutMode, type Widget, type WidgetDataPoint, type WidgetSpaceDetails } from '../../models';
import { LayoutHelper } from '../layout/config';

@Component({
  imports: [
    BuildingPlanComponent,
    CommonModule,
    ConnectedDevicesComponent,
    DeviceChartComponent,
    MatTooltip,
    NgClass,
    NgxSkeletonLoaderModule,
    RouterLink,
    SpaceLocationComponent,
    DataPointComponent,
    DevicesComponent,
    DeviceStatsComponent,
    MapComponent,
    SpaceDetailsComponent,
    GaugeComponent,
    RoomStateComponent,
  ],
  selector: 'avs-li-widget-container',
  styleUrl: './widget-container.component.scss',
  templateUrl: './widget-container.component.html',
})
export class WidgetContainerComponent {
  clientId = input<string>();
  delete = output<number>();
  edit = output<number>();
  mode = input.required<LayoutMode>();
  widget = input.required<Widget>();

  protected heightClassBreakpoint = signal<string>('');
  protected widthClassBreakpoint = signal<string>('');
  protected isEditMode = computed(() => this.mode() === 'edit');
  protected queryParams = computed(() => ({ clientId: this.clientId() }));

  protected spaceUrl = computed(() => {
    const config = this.widget().config;

    if (config.type === 'connectedDevices') {
      return null;
    }

    const locationConfig = config as WidgetSpaceDetails;

    if (locationConfig.roomId) {
      return ['/dashboard', locationConfig.locationId, locationConfig.floorId, locationConfig.roomId];
    }

    if (locationConfig.floorId && !locationConfig.roomId) {
      return ['/dashboard', locationConfig.locationId, locationConfig.floorId];
    }

    return ['/dashboard', locationConfig.locationId];
  });

  protected titleUrl = computed(() => {
    if (this.widget().config.type === 'connectedDevices') {
      return null;
    }

    if ((this.widget().config as WidgetDataPoint).deviceId) {
      return ['/devices', this.widget().config.locationId, (this.widget().config as WidgetDataPoint).deviceId];
    }

    return this.spaceUrl();
  });

  protected widgetRef = viewChild<{ refresh?: () => void; }>('widgetRef');

  private el = inject<ElementRef<HTMLElement>>(ElementRef);

  itemChanged() {
    const { height, width } = this.el.nativeElement.getBoundingClientRect();

    this.heightClassBreakpoint.set(LayoutHelper.getClassBreakpoint(height, 'height'));
    this.widthClassBreakpoint.set(LayoutHelper.getClassBreakpoint(width, 'width'));
    this.widgetRef()?.refresh?.();
  }
}
